import { CONTACT_TYPE_ID, denormalizeTypeId, SUBSCRIPTION_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { CONTACT } from 'customer-data-objects/constants/ObjectTypes';
import { HIDDEN } from 'customer-data-objects/property/AccessLevel';
import { useAccessLevel } from 'customer-data-properties/accessLevel/AccessLevelContext';
import FormattedPropertyV2 from 'customer-data-properties/formatted/FormattedPropertyV2';

// @ts-expect-error file not migrated yet
import FormattedJobTitle from 'customer-data-sidebar/universal/formatterComponents/FormattedJobTitle';
import CrmLogger from 'customer-data-tracking/loggers/CrmLogger';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useCallback } from 'react';
import getIn from 'transmute/getIn';
import { Button } from 'foundations-components/transitional/components/Button';
import { Small } from 'foundations-components/transitional/components/Small';
import { Link } from 'foundations-components/transitional/components/Link';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { List } from 'foundations-components/transitional/components/List';
import { IconCircle } from 'foundations-components/transitional/components/IconCircle';
import { ExclamationIcon } from 'foundations-assets/icons/components/ExclamationIcon';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import PropertyCopy from '../../../card/secondaryProperties/PropertyCopy';
import { SecondaryPropertyDisplay } from '../../../card/secondaryProperties/SecondaryPropertyDisplay';
import { getPropertyValueFromProperties, getPropertyDefinition } from '../../../helpers/propertiesHelpers';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useGetContactChickletSecondaryDisplayLabels = ({
  properties,
  object,
  toObjectTypeId,
  propertyDefinitions,
  onEmailClick,
  onPhoneClick,
  fromObjectTypeId,
  objectId
}) => {
  const contactPhone = getPropertyValueFromProperties(properties, 'phone');
  const contactEmail = getPropertyValueFromProperties(properties, 'email');
  const getAdditionalPropertyValue = propertyName => getPropertyValueFromProperties(properties, propertyName);
  const handlePhoneClick = useCallback(() => {
    if (typeof onPhoneClick === 'function') {
      onPhoneClick(denormalizeTypeId(toObjectTypeId), String(objectId), 'phone');
    }
    CrmLogger.log('sidebar-interaction', {
      type: CONTACT,
      action: 'clicked hyperlinked action from card',
      subAction: 'phone'
    });
  }, [objectId, onPhoneClick, toObjectTypeId]);
  const handleEmailClick = useCallback(e => {
    if (typeof onEmailClick === 'function') {
      // prevent link from opening email client since we're trying to
      // override that behavior with onEmailClick, we still render it as a
      // link to get the benefits of the "mailto:" href, i.e. being able to
      // right click and copy the whole email address
      e.preventDefault();
      onEmailClick(denormalizeTypeId(toObjectTypeId), String(objectId));
    }
    CrmLogger.log('sidebar-interaction', {
      type: CONTACT,
      action: 'clicked hyperlinked action from card',
      subAction: 'email'
    });
  }, [objectId, onEmailClick, toObjectTypeId]);
  const contactPhoneAccessLevel = useAccessLevel({
    objectType: toObjectTypeId,
    propertyName: 'phone'
  });
  const renderEmailWithTooltip = emailComponent => {
    return /*#__PURE__*/_jsx(Tooltip, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "cardAssociatedObjects.ContactSecondaryProperties.emailTooltip"
      }),
      placement: "bottom left",
      children: emailComponent
    });
  };
  const renderEmail = formattedEmail => {
    const renderedEmail = /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx(Link, {
        href: `mailto:${contactEmail}`,
        "data-selenium-test": "contact-chicklet-email",
        "data-test-id": "contact-chicklet-email",
        onClick: handleEmailClick,
        external: typeof onEmailClick !== 'function',
        truncate: true,
        children: formattedEmail
      })
    });
    return typeof onEmailClick === 'function' ? renderEmailWithTooltip(renderedEmail) : renderedEmail;
  };
  const renderContactEmail = () => !contactEmail ? /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(SecondaryPropertyDisplay, {
      propertyLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "cardAssociatedObjects.ContactSecondaryPropertyLabels.emptyEmail"
      })
    }), fromObjectTypeId === SUBSCRIPTION_TYPE_ID && /*#__PURE__*/_jsx(List, {
      inline: true,
      "data-selenium-test": "no-email-associated-icon",
      "data-test-id": "no-email-associated-icon",
      children: /*#__PURE__*/_jsx(Tooltip, {
        placement: "right",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "cardAssociatedObjects.ContactSecondaryProperties.emptyEmailTooltipMessage"
        }),
        children: /*#__PURE__*/_jsx(IconCircle, {
          icon: ExclamationIcon,
          backgroundColor: "fill-primary-default",
          color: "icon-core-on-fill-default",
          size: "100",
          padding: "100"
        })
      })
    })]
  }) : /*#__PURE__*/_jsx("div", {
    "data-selenium-test": "contact-chicklet-email",
    "data-test-id": "contact-chicklet-email",
    children: /*#__PURE__*/_jsx(PropertyCopy, {
      propertyName: "email",
      data: renderEmail(contactEmail)
    })
  });
  const renderPhoneWithTooltip = formattedPhonePropertyValue => {
    return /*#__PURE__*/_jsx(Tooltip, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "cardAssociatedObjects.ContactSecondaryProperties.phoneTooltip"
      }),
      placement: "bottom left",
      children: /*#__PURE__*/_jsx(Button, {
        "data-selenium-test": "contact-chicklet-phone",
        "data-test-id": "contact-chicklet-phone",
        "data-unit-test": "contact-chicklet-phone",
        use: "link",
        onClick: handlePhoneClick,
        children: /*#__PURE__*/_jsx(TruncateString, {
          useFlex: true,
          children: formattedPhonePropertyValue
        })
      })
    });
  };
  const renderPhoneFormattedProperty = propertyDefinition => /*#__PURE__*/_jsx(FormattedPropertyV2, {
    objectTypeId: CONTACT_TYPE_ID,
    objectId: `${objectId}`,
    value: contactPhone,
    getDraftValue: () => undefined,
    getSavedValue: getAdditionalPropertyValue
    // @ts-expect-error Known property issue as they have not updated the type definitions
    // https://hubspot.slack.com/archives/C025K57CMMG/p1696524684019289?thread_ts=1696349045.450059&cid=C025K57CMMG
    ,
    property: propertyDefinition
  });

  // This UITruncateString is needed in order to handle breaking unicode characters
  // see: https://issues.hubspotcentral.com/browse/CRM-29056
  const renderPhoneNumber = propertyDefinition => {
    if (!contactPhone) {
      return /*#__PURE__*/_jsx(SecondaryPropertyDisplay, {
        propertyLabel: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "cardAssociatedObjects.ContactSecondaryPropertyLabels.emptyPhone"
        })
      });
    }
    const formattedPhonePropertyValue = renderPhoneFormattedProperty(propertyDefinition);
    if (contactPhoneAccessLevel === HIDDEN) {
      return null;
    }
    const shouldRenderPhoneTooltip = typeof onPhoneClick === 'function';
    return /*#__PURE__*/_jsx("div", {
      "data-selenium-test": "contact-chicklet-phone",
      "data-test-id": "contact-chicklet-phone",
      children: /*#__PURE__*/_jsx(PropertyCopy, {
        "data-unit-test": "phoneNumber",
        propertyName: "phone",
        data: shouldRenderPhoneTooltip ? renderPhoneWithTooltip(formattedPhonePropertyValue) : formattedPhonePropertyValue
      })
    });
  };
  const getAssociatedCompanyName = () => {
    if (!('defaultAssociations' in object) || !object.defaultAssociations.toCompanies || object.defaultAssociations.toCompanies.edges.length === 0) {
      return null;
    }
    const associatedCompanies = object.defaultAssociations.toCompanies.edges;
    const company = associatedCompanies[0];
    return getIn(['name', 'value'], company.node.defaultProperties);
  };
  const renderJobTitle = () => {
    const companyName = getAssociatedCompanyName();
    const jobTitle = getPropertyValueFromProperties(properties, 'jobtitle');
    if (!companyName && !jobTitle) {
      return null;
    }
    const jobTitleDefinition = getPropertyDefinition('jobtitle', propertyDefinitions);
    const jobTitleHidden = jobTitle && jobTitleDefinition && 'hidden' in jobTitleDefinition ? jobTitleDefinition.hidden : false;
    return /*#__PURE__*/_jsx("div", {
      "data-selenium-test": "contact-chicklet-job-title",
      "data-test-id": "contact-chicklet-job-title",
      children: /*#__PURE__*/_jsx(Small, {
        use: "help",
        marginBlockEnd: "100",
        children: /*#__PURE__*/_jsx(FormattedJobTitle, {
          companyName: companyName,
          jobTitle: jobTitle || '',
          jobTitleHidden: jobTitleHidden
        })
      })
    });
  };
  const propertyFormatters = {
    email: renderContactEmail,
    phone: renderPhoneNumber
  };
  return {
    propertyFormatters,
    copyDisabled: true,
    additionalPrefixContent: renderJobTitle()
  };
};
export default useGetContactChickletSecondaryDisplayLabels;